import React, { useState, useEffect } from "react";
import clsx from "clsx";
import dayjs from "dayjs";

import { styled } from "@mui/styles";
import {
  Card,
  Grid,
  Avatar,
  Typography,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import GpsFixedRoundedIcon from "@mui/icons-material/GpsFixedRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AirlineSeatReclineNormalRoundedIcon from "@mui/icons-material/AirlineSeatReclineNormalRounded";
import MeetingRoomRoundedIcon from "@mui/icons-material/MeetingRoomRounded";
import EmojiPeopleRoundedIcon from "@mui/icons-material/EmojiPeopleRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";

require("dayjs/locale/th");
dayjs.locale("th");

const useStyles = makeStyles((theme) => ({
  DivCoverReturn: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  Grid: {
    width: "80%",
    height: "100%",
    margin: "0 auto",
    marginTop: "20px",
    ["@media only screen and (max-width: 600px)"]: {
      width: "100%",
    },
  },
  Title: {
    textAlign: "center",
    fontSize: "25px",
    margin: "25px 0px",
  },
  TableRow: {
    ["&:hover"]: {
      cursor: "pointer",
    },
  },
}));

const CardStyled = styled(Card)(({ theme }) => ({
  marginBottom: "20px",
  display: "flex",
  alignContent: "center",
  alignItems: "center",
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down("sm")]: {
    display: "block",
  },
  paddingBottom: "10px",
}));

const GridContainer = styled(Grid)(({ theme }) => ({
  width: "80%",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const AvatarStyled = styled(Avatar)(({ theme }) => ({
  width: "40px",
  height: "40px",
  margin: "5px",
  [theme.breakpoints.down("lg")]: {
    width: "80px",
    height: "80px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "140px",
    height: "140px",
    marginBottom: "0px",
  },
}));

const NameAndType = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});

const TextAndIcon = styled("div")({
  display: "flex",
  marginBottom: "5px",
  "& .Icon": {
    marginRight: "10px",
    color: "#293A80",
  },
  "& .CrossArea": {
    color: "#C400C6 !important",
  },
  "& .CrossAreaPool": {
    color: "#FF6600 !important",
  },
  "& .InArea": {
    color: "#0A81AB !important",
  },
  "& .Delivery": {
    color: "#8B4513 !important",
  },
});

const Text = styled(Typography)({
  color: "#293A80",
});

const DateAndTime = styled("div")({
  "@media (min-width: 700px) and (max-width: 780px)": {
    display: "flex",
  },
});

const DivStatus = styled("div")(({ theme }) => ({
  width: "150px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingTop: "5px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "center",
  },
  "& .TextApproved": {
    color: "#6DCC6D",
  },
  "& .TextRejected": {
    color: "#EA4647",
  },
  "& .TextWaiting": {
    color: "#C3B816",
  },
  "& .TextApprovedForDriverBooking": {
    color: "#E30058",
    marginLeft: "5px",
  },
}));

const DivAvatarAndName = styled("div")(({ theme }) => ({
  display: "flex",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
  },
}));

const TextFirstnameAndLastName = styled("div")(({ theme }) => ({
  display: "flex",
  [theme.breakpoints.down("sm")]: {
    display: "block",
    paddingTop: "10px",
  },
  "& .FirstName": {
    marginRight: "10px",
    [theme.breakpoints.down("sm")]: {
      marginBottom: "0",
    },
  },
}));

const Name = styled(Typography)({
  color: "#185ADB",
  fontSize: "20px",
  marginBottom: "10px",
});

const IconStatus = styled(Avatar)(({ theme }) => ({
  width: "40px",
  height: "40px",
  [theme.breakpoints.down("sm")]: {
    marginRight: "20px",
  },
}));

const TextStatus = styled(Typography)({
  fontSize: "15px",
  fontWeight: "bold",
});

const StyledCellHeader = styled(TableCell)({
  borderBottom: "none",
  color: "#637381",
  backgroundColor: "#f4f6f8",
  padding: "8px 14px",
  "&:first-of-type": {
    paddingLeft: 24,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  "&:last-of-type": {
    paddingRight: 24,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
});
const StyledCellContent = styled(TableCell)(({ theme }) => ({
  borderBottom: "1px dashed rgba(224, 224, 224, 1)",
  padding: "8px 14px",
  "&:first-of-type": {
    paddingLeft: 24,
  },
  "&:last-of-type": {
    paddingRight: 24,
  },
}));
const CardRequest = (props) => {
  const classes = useStyles();
  const mobile = useMediaQuery("(max-width:600px)");
  const [booking, setBooking] = useState(null);
  const [showHistory, setHistory] = useState(0);

  useEffect(() => {
    if (props.booking) {
      setBooking(props.booking);
    }
  }, []);
  const verificationTypeOfBooking = (type) => {
    if (verifyTypeOfBookingCrossArea(type)) {
      return "การจองรถข้ามพื้นที่เฉพาะ";
    }
    if (verifyTypeOfBookingCrossAreaPool(type)) {
      return "การจองรถข้ามพื้นที่ Pool";
    }
    if (verifyTypeOfBookingInArea(type)) {
      return "การจองรถในพื้นที่";
    }
    if (verifyTypeOfBookingDeliveryCar(type)) {
      return "การขอรถรับส่งของ";
    }
    if (verifyTypeOfBookingMeetingRoom(type)) {
      return "การขอจองห้องประชุม";
    }
    if (verifyTypeOfBookingDriverBooking(type)) {
      return "การขอจองคนขับรถ";
    }
  };

  const verifyTypeOfStatus = (statusApproved, Approved, statusManageCar) => {
    // console.log(statusApproved, Approved, statusManageCar);
    return (
      <div style={{ display: "flex" }}>
        {verifyTpyeOfStatusIsApproved(
          statusApproved,
          Approved,
          statusManageCar
        ) ? (
          <CheckCircleRoundedIcon style={{ color: "#6DCC6D" }} />
        ) : verifyTpyeOfStatusIsRejected(statusApproved, Approved) ? (
          <CancelRoundedIcon style={{ color: "#EA4647" }} />
        ) : (
          <HourglassTopRoundedIcon style={{ color: "orange" }} />
        )}

        <TextStatus
          className={clsx({
            TextApproved: verifyTpyeOfStatusIsApproved(
              statusApproved,
              Approved,
              statusManageCar
            ),
            TextRejected: verifyTpyeOfStatusIsRejected(
              statusApproved,
              Approved
            ),
            TextWaiting:
              !verifyTpyeOfStatusIsApproved(
                statusApproved,
                Approved,
                statusManageCar
              ) && !verifyTpyeOfStatusIsRejected(statusApproved, Approved),
          })}
        >
          {verifyTpyeOfStatusIsApproved(
            statusApproved,
            Approved,
            statusManageCar
          ) ? (
            <Text style={{ color: "#6DCC6D" }}>อนุมัติแล้ว</Text>
          ) : verifyTpyeOfStatusIsRejected(statusApproved, Approved) ? (
            <Text style={{ color: "#EA4647" }}>ไม่อนุมัติ</Text>
          ) : (
            <Text style={{ color: "darkorange" }}>รออนุมัติ</Text>
          )}
        </TextStatus>
      </div>
    );
  };

  const verifyTypeOfStatusForDriverBooking = (
    statusDelivery,
    statusManageCar
  ) => {
    return (
      <>
        <IconStatus
          src={`../assets/Image/Icon/${
            verifyTpyeOfStatusIsApprovedForDriverBooking(
              statusDelivery,

              statusManageCar
            )
              ? "carWaiting.png"
              : verifyTpyeOfStatusIsWaitingForDriverBooking(statusManageCar)
              ? "waiting.png"
              : "waiting.png"
          }`}
          alt="IconStatus"
        />
        <TextStatus
          className={clsx({
            TextApprovedForDriverBooking:
              verifyTpyeOfStatusIsApprovedForDriverBooking(
                statusDelivery,

                statusManageCar
              ),

            TextWaiting: verifyTpyeOfStatusIsWaitingForDriverBooking(
              statusDelivery,
              statusManageCar
            ),
          })}
        >
          {verifyTpyeOfStatusIsApprovedForDriverBooking(
            statusDelivery,

            statusManageCar
          )
            ? "Approved"
            : verifyTpyeOfStatusIsWaitingForDriverBooking(
                statusDelivery,
                statusManageCar
              )
            ? "Waiting"
            : ""}
        </TextStatus>
      </>
    );
  };

  const verifyTypeOfBookingCrossArea = (typeOfBooking) => {
    if (typeOfBooking === "CrossArea") {
      return true;
    } else {
      return false;
    }
  };

  const verifyTypeOfBookingCrossAreaPool = (typeOfBooking) => {
    if (typeOfBooking === "CrossAreaPool") {
      return true;
    } else {
      return false;
    }
  };

  const verifyTypeOfBookingInArea = (typeOfBooking) => {
    if (typeOfBooking === "InArea") {
      return true;
    } else {
      return false;
    }
  };

  const verifyTypeOfBookingDeliveryCar = (typeOfBooking) => {
    if (typeOfBooking === "Delivery") {
      return true;
    } else {
      return false;
    }
  };
  const verifyTypeOfBookingMeetingRoom = (typeOfBooking) => {
    if (typeOfBooking === "MeetingRoom") {
      return true;
    } else {
      return false;
    }
  };
  const verifyTypeOfBookingDriverBooking = (typeOfBooking) => {
    if (typeOfBooking === "DriverBooking") {
      return true;
    } else {
      return false;
    }
  };

  const verifyTpyeOfStatusIsApproved = (
    statusApproved,
    approved,
    statusManageCar
  ) => {
    if (
      statusApproved === "Success" &&
      approved === "Success" &&
      statusManageCar === "Success"
    ) {
      return true;
    } else {
      return false;
    }
  };

  const verifyTpyeOfStatusIsRejected = (statusApproved, approved) => {
    if (statusApproved === "Success" && approved === false) {
      return true;
    } else {
      return false;
    }
  };

  const verifyTpyeOfStatusIsApprovedForDriverBooking = (
    statusDelivery,
    statusManageCar
  ) => {
    if (statusManageCar === "Success" && statusDelivery != "Success") {
      return true;
    } else {
      return false;
    }
  };
  const verifyTpyeOfStatusIsWaitingForDriverBooking = (
    statusDelivery,
    statusManageCar
  ) => {
    if (statusManageCar != "Success" && statusDelivery != "Success") {
      return true;
    } else {
      return false;
    }
  };

  const TextTypeBooking = () => {
    return (
      <>
        {verifyTypeOfBookingCrossArea(props.typeOfBooking) && (
          <DriveEtaIcon className="CrossArea Icon" />
        )}
        {verifyTypeOfBookingCrossAreaPool(props.typeOfBooking) && (
          <EmojiTransportationIcon className="CrossAreaPool Icon" />
        )}
        {verifyTypeOfBookingInArea(props.typeOfBooking) && (
          <EmojiTransportationIcon className="InArea Icon" />
        )}
        {verifyTypeOfBookingDeliveryCar(props.typeOfBooking) && (
          <EmojiTransportationIcon className="Delivery Icon" />
        )}
        {verifyTypeOfBookingMeetingRoom(props.typeOfBooking) && (
          <MeetingRoomRoundedIcon className="MeetingRoom Icon" />
        )}
        {verifyTypeOfBookingDriverBooking(props.typeOfBooking) && (
          <EmojiPeopleRoundedIcon className="DriverBooking Icon" />
        )}
        <Typography
          className={clsx({
            CrossArea: verifyTypeOfBookingCrossArea(props.typeOfBooking),
            CrossAreaPool: verifyTypeOfBookingCrossAreaPool(
              props.typeOfBooking
            ),
            InArea: verifyTypeOfBookingInArea(props.typeOfBooking),
            Delivery: verifyTypeOfBookingDeliveryCar(props.typeOfBooking),
            MeetingRoom: verifyTypeOfBookingMeetingRoom(props.typeOfBooking),
            DriverBooking: verifyTypeOfBookingDriverBooking(
              props.typeOfBooking
            ),
          })}
        >
          {`${verificationTypeOfBooking(props.typeOfBooking)}`}
        </Typography>
      </>
    );
  };

  return (
    <Box>
      {props.booking && props.booking.length > 0 && (
        <TableContainer style={{ minHeight: 400 }}>
          {console.log(props.booking)}
          <Table>
            <TableHead>
              <TableRow>
                <StyledCellHeader>ชื่อ</StyledCellHeader>
                <StyledCellHeader>ประเภท</StyledCellHeader>
                {props.typeOfBooking !== "CrossAreaPool" &&
                  props.typeOfBooking !== "MeetingRoom" &&
                  props.typeOfBooking !== "DriverBooking" && (
                    <StyledCellHeader>วัตถุประสงค์</StyledCellHeader>
                  )}
                <StyledCellHeader>วันที่จอง</StyledCellHeader>
                <StyledCellHeader>เวลา</StyledCellHeader>
                {props.typeOfBooking === "DriverBooking" && (
                  <StyledCellHeader>ต้นทาง</StyledCellHeader>
                )}
                {props.typeOfBooking === "DriverBooking" && (
                  <StyledCellHeader>ปลายทาง</StyledCellHeader>
                )}
                <StyledCellHeader>สถานะ</StyledCellHeader>
              </TableRow>
            </TableHead>
            {props.typeOfBooking != "MeetingRoom" &&
              props.typeOfBooking != "DriverBooking" && (
                <TableBody>
                  {props.booking.map((booking, index) => (
                    <TableRow
                      hover
                      key={index}
                      className={`${classes.TableRow}`}
                      onClick={() => {
                        if (props.typeOfBooking === "CrossArea") {
                          props.onClickCardHandler(
                            props.typeOfBooking,
                            booking.idCrossAreaCarBooking
                          );
                        } else if (props.typeOfBooking === "CrossAreaPool") {
                          props.onClickCardHandler(
                            props.typeOfBooking,
                            booking.idCrossAreaCarPoolBooking
                          );
                        } else if (props.typeOfBooking === "InArea") {
                          props.onClickCardHandler(
                            props.typeOfBooking,
                            booking.idinAreaCarBooking
                          );
                        } else if (props.typeOfBooking === "Delivery") {
                          props.onClickCardHandler(
                            props.typeOfBooking,
                            booking.idDeliveryCarBooking
                          );
                          // } else if (props.typeOfBooking === "MeetingRoom") {
                          //   props.onClickCardHandler(
                          //     props.typeOfBooking,
                          //     booking.idMeetingRoom,
                          //     booking.idMeetingRoom
                          //   );
                          // } else if (props.typeOfBooking === "DriverBooking") {
                          //   props.onClickCardHandler(
                          //     props.typeOfBooking,
                          //     booking.idDriverBooking
                          //   );
                        }
                      }}
                    >
                      <StyledCellContent>
                        <DivAvatarAndName>
                          <AvatarStyled
                            src={`${process.env.REACT_APP_URL}image/profile/1.jpg`}
                            alt={booking.name}
                          />
                          <NameAndType>
                            <TextFirstnameAndLastName>
                              {booking.name}
                            </TextFirstnameAndLastName>
                          </NameAndType>{" "}
                        </DivAvatarAndName>
                      </StyledCellContent>
                      <StyledCellContent>
                        <TextAndIcon>{TextTypeBooking()}</TextAndIcon>
                      </StyledCellContent>
                      {!verifyTypeOfBookingCrossAreaPool(
                        props.typeOfBooking
                      ) && (
                        <StyledCellContent>
                          <TextAndIcon>
                            <GpsFixedRoundedIcon className="Icon" />
                            <Text>{booking.purpose}</Text>
                          </TextAndIcon>
                        </StyledCellContent>
                      )}
                      <StyledCellContent>
                        <TextAndIcon>
                          <CalendarTodayIcon className="Icon" />
                          <Text>
                            {dayjs(booking.departureDate).format(
                              "DD MMMM YYYY"
                            )}
                          </Text>
                        </TextAndIcon>
                      </StyledCellContent>
                      <StyledCellContent>
                        <TextAndIcon>
                          <AccessTimeRoundedIcon className="Icon" />
                          <Text>
                            {`${dayjs(booking.startTime).format(
                              "HH:mm"
                            )} - ${dayjs(booking.endTime).format("HH:mm")} น.`}
                          </Text>
                        </TextAndIcon>
                      </StyledCellContent>
                      <StyledCellContent>
                        {verifyTypeOfStatus(
                          booking.statusApproved,
                          booking.Approved,
                          booking.statusManageCar
                        )}
                      </StyledCellContent>
                      {/* <StyledCellContent>{booking.toPlaceName}</StyledCellContent>
    
                        <StyledCellContent>
                          {booking.status === "ได้รับสินค้าเรียบร้อย" && (
                            <Name
                              style={{ color: "#24944f", fontSize: "16px" }}
                            >{`${booking.status}`}</Name>
                          )}
                          {booking.status === "รอรับสินค้า" && (
                            <Name
                              style={{ color: "#d42121", fontSize: "16px" }}
                            >{`${booking.status}`}</Name>
                          )}
                          {booking.status === "รับสินค้าเรียบร้อย" && (
                            <Name
                              style={{ color: "#dfcf20", fontSize: "16px" }}
                            >{`${booking.status}`}</Name>
                          )}
                          {booking.status === "ส่งสินค้าเรียบร้อย" && (
                            <Name
                              style={{ color: "#d47721", fontSize: "16px" }}
                            >{`${booking.status}`}</Name>
                          )}
                        </StyledCellContent> */}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            {props.typeOfBooking === "MeetingRoom" && (
              <TableBody>
                {props.booking.map((booking, index) => (
                  <TableRow
                    hover
                    key={index}
                    className={`${classes.TableRow}`}
                    onClick={() => {
                      if (props.typeOfBooking === "CrossArea") {
                        props.onClickCardHandler(
                          props.typeOfBooking,
                          booking.idCrossAreaCarBooking
                        );
                      } else if (props.typeOfBooking === "CrossAreaPool") {
                        props.onClickCardHandler(
                          props.typeOfBooking,
                          booking.idCrossAreaCarPoolBooking
                        );
                      } else if (props.typeOfBooking === "InArea") {
                        props.onClickCardHandler(
                          props.typeOfBooking,
                          booking.idinAreaCarBooking
                        );
                      } else if (props.typeOfBooking === "Delivery") {
                        props.onClickCardHandler(
                          props.typeOfBooking,
                          booking.idDeliveryCarBooking
                        );
                      } else if (props.typeOfBooking === "MeetingRoom") {
                        props.onClickCardHandler(
                          props.typeOfBooking,
                          booking.idMeetingRoom,
                          booking.idMeetingRoom
                        );
                      } else if (props.typeOfBooking === "DriverBooking") {
                        props.onClickCardHandler(
                          props.typeOfBooking,
                          booking.idDriverBooking
                        );
                      }
                    }}
                  >
                    <StyledCellContent>
                      <DivAvatarAndName>
                        <AvatarStyled
                          src={`${process.env.REACT_APP_URL}image/profile/1.jpg`}
                          alt={booking.nameUser}
                        />
                        <NameAndType>
                          <TextFirstnameAndLastName>
                            {booking.nameUser}
                          </TextFirstnameAndLastName>
                        </NameAndType>{" "}
                      </DivAvatarAndName>
                    </StyledCellContent>
                    <StyledCellContent>
                      <TextAndIcon>{TextTypeBooking()}</TextAndIcon>
                    </StyledCellContent>
                    {/* {!verifyTypeOfBookingCrossAreaPool(
                              props.typeOfBooking
                            ) && (
                                <StyledCellContent>
                              <TextAndIcon>
                                <GpsFixedRoundedIcon className="Icon" />
                                <Text>{booking.purpose}</Text>
                              </TextAndIcon>
                        </StyledCellContent>
                            )} */}
                    <StyledCellContent>
                      <TextAndIcon>
                        <CalendarTodayIcon className="Icon" />
                        <Text>
                          {dayjs(booking.startDate).format("DD MMMM YYYY")}
                        </Text>
                      </TextAndIcon>
                    </StyledCellContent>
                    <StyledCellContent>
                      <TextAndIcon>
                        <AccessTimeRoundedIcon className="Icon" />
                        <Text>
                          {`${booking.timeStart} - ${booking.timeEnd} น.`}
                        </Text>
                      </TextAndIcon>
                    </StyledCellContent>
                    <StyledCellContent>
                      {verifyTypeOfStatus(
                        booking.statusApproved,
                        booking.Approved,
                        booking.statusManageCar
                      )}
                    </StyledCellContent>
                  </TableRow>
                ))}
              </TableBody>
            )}
            {props.typeOfBooking === "DriverBooking" && (
              <TableBody>
                {props.booking.map((booking, index) => (
                  <TableRow
                    hover
                    key={index}
                    className={`${classes.TableRow}`}
                    onClick={() => {
                      if (props.typeOfBooking === "DriverBooking") {
                        props.onClickCardHandler(
                          props.typeOfBooking,
                          booking.idDriverBooking
                        );
                      }
                    }}
                  >
                    <StyledCellContent>
                      <DivAvatarAndName>
                        <AvatarStyled
                          src={`${process.env.REACT_APP_URL}image/profile/1.jpg`}
                          alt={booking.nameUser}
                        />
                        <NameAndType>
                          <TextFirstnameAndLastName>
                            {booking.nameUser}
                          </TextFirstnameAndLastName>
                        </NameAndType>{" "}
                      </DivAvatarAndName>
                    </StyledCellContent>
                    <StyledCellContent>
                      <TextAndIcon>{TextTypeBooking()}</TextAndIcon>
                    </StyledCellContent>
                    {/* {!verifyTypeOfBookingCrossAreaPool(
                              props.typeOfBooking
                            ) && (
                                <StyledCellContent>
                              <TextAndIcon>
                                <GpsFixedRoundedIcon className="Icon" />
                                <Text>{booking.purpose}</Text>
                              </TextAndIcon>
                        </StyledCellContent>
                            )} */}
                    <StyledCellContent>
                      <TextAndIcon>
                        <CalendarTodayIcon className="Icon" />
                        <Text>
                          {dayjs(booking.startDate).format("DD MMMM YYYY")}
                        </Text>
                      </TextAndIcon>
                    </StyledCellContent>
                    <StyledCellContent>
                      <TextAndIcon>
                        <AccessTimeRoundedIcon className="Icon" />
                        <Text>
                          {`${booking.startTime} - ${booking.endTime} น.`}
                        </Text>
                      </TextAndIcon>
                    </StyledCellContent>
                    <StyledCellContent>
                      {booking.namePlaceFrom}
                    </StyledCellContent>
                    <StyledCellContent>{booking.namePlaceTo}</StyledCellContent>
                    <StyledCellContent>
                      {verifyTypeOfStatusForDriverBooking(
                        booking.statusDelivery,
                        booking.statusManageCar
                      )}
                    </StyledCellContent>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CardRequest;
